import { space } from '@design/templateFns';
import {
  button,
  iconOnly,
  primaryRed,
  primaryWhite,
  secondary,
  secondaryGray,
  secondaryRed,
  secondaryWhite,
  tertiary,
  tertiaryBlack,
  tertiaryGray,
  tertiaryGrayDisabled,
  tertiaryUnderline,
  tertiaryWhite,
} from '@design/variants/buttons';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Close,
  Download,
  Email,
  ExternalLink,
  Facebook,
  FacebookColor,
  Filter,
  GoogleColor,
  Insights,
  Magnifier,
  Phone,
  RightArrow,
  ScheduleUpdate,
  Share,
  Star,
} from '@icon/index';
import Link from 'next/link';
import { FC, MouseEventHandler } from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';

export type ButtonVariants =
  | 'button'
  | 'primaryWhite'
  | 'primaryRed'
  | 'secondaryRed'
  | 'secondary'
  | 'tertiary'
  | 'secondaryWhite'
  | 'tertiary'
  | 'tertiaryBlack'
  | 'tertiaryGray'
  | 'tertiaryGrayDisabled'
  | 'tertiaryWhite'
  | 'tertiaryUnderline'
  | 'iconOnly';

export type IconVariants =
  | 'Close'
  | 'Star'
  | 'Magnifier'
  | 'Download'
  | 'Phone'
  | 'Email'
  | 'ExternalLink'
  | 'Filter'
  | 'ScheduleUpdate'
  | 'Facebook'
  | 'FacebookColor'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'ArrowDown'
  | 'GoogleColor'
  | 'Share'
  | 'Insights';

export interface ButtonProps {
  variant: ButtonVariants;
  // For URLs
  url?: string;
  // For click events
  onClickHandler?: MouseEventHandler<HTMLButtonElement>;
  label: string;
  arrow?: Boolean;
  arrowColor?: string;
  icon?: IconVariants;
  iconPosition?: string;
  iconSize?: string;
  target?: boolean;
  type?: 'submit' | 'reset' | 'button';
  disabled?: boolean;
  form?: string;
  dataTestId?: string;
  style?: Record<string, string | number>;
  justifyContent?: string;
  alignItems?: string;
  className?: string;
}

/*** Icons ***/

const iconComps = {
  Close,
  Star,
  Magnifier,
  Download,
  Phone,
  Email,
  ExternalLink,
  Filter,
  ScheduleUpdate,
  Share,
  Insights,
  Facebook,
  FacebookColor,
  ArrowLeft,
  ArrowRight,
  ArrowDown,
  GoogleColor,
};
// render icon if it exists, otherwise return null
export const RenderIcon = (icon: IconVariants, size?: string) => {
  const Icon = iconComps[icon];
  return Icon ? <Icon size={size ?? '20px'} /> : null;
};

/*** STYLING ***/

const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['$variant'].includes(prop),
})<{ $variant: ButtonVariants }>`
  ${variant({
    variants: {
      button,
      primaryWhite,
      primaryRed,
      secondaryRed,
      secondary,
      secondaryGray,
      secondaryWhite,
      tertiary,
      tertiaryGray,
      tertiaryGrayDisabled,
      tertiaryBlack,
      tertiaryWhite,
      tertiaryUnderline,
      iconOnly,
    },
  })}
`;

const StyledSpan = styled.span.withConfig({
  shouldForwardProp: (prop) => !['$variant'].includes(prop),
})<{ $variant: ButtonVariants }>`
  ${variant({
    variants: {
      button,
      primaryWhite,
      primaryRed,
      secondaryRed,
      secondary,
      secondaryGray,
      secondaryWhite,
      tertiary,
      tertiaryGray,
      tertiaryGrayDisabled,
      tertiaryBlack,
      tertiaryWhite,
      tertiaryUnderline,
      iconOnly,
    },
  })}
`;

// Custom Link component that uses Next.js Link for navigation and styled-components for styling
const StyledButtonLink = ({ href, children, ...props }) => {
  return (
    <Link href={href} passHref>
      <StyledSpan $variant={props.variant} {...props}>
        {children}
      </StyledSpan>
    </Link>
  );
};

const StyledRegularButton = ({ ...props }) => {
  return <StyledButton $variant={props.variant} {...props} />;
};

const BtnLinkWrapper = styled.span<{
  $iconPosition?: string;
  $justifyContent?: string;
  $alignItems?: string;
}>`
  display: flex;
  align-items: ${({ $alignItems }) => $alignItems};
  justify-content: ${({ $justifyContent }) => $justifyContent};
  gap: ${space('sm')};
  cursor: pointer;

  // change cursur to default if button is disabled
  button[disabled] & {
    cursor: default;
  }

  & > span {
    display: inline-block;
    white-space: nowrap;
  }

  svg {
    display: flex;
    // get order from props
    order: ${({ $iconPosition }) => ($iconPosition === 'left' ? '-1' : 'initial')};
  }
`;

export const Button: FC<ButtonProps> = ({
  variant = 'button',
  url,
  onClickHandler,
  label = 'Learn About Us',
  arrow = false,
  icon,
  iconPosition,
  iconSize,
  target,
  type = 'button',
  disabled,
  style,
  form,
  dataTestId,
  justifyContent = 'center',
  alignItems = 'center',
  className,
}) => {
  return (
    <>
      {url ? (
        <StyledButtonLink
          data-testid={dataTestId}
          className={className}
          href={url}
          variant={variant}
          target={target ? '_blank' : '_self'}
          style={style}
        >
          <BtnLinkWrapper
            $iconPosition={iconPosition}
            $justifyContent={justifyContent}
            $alignItems={alignItems}
          >
            {variant === 'iconOnly' ? (
              <span className="visually-hidden">{label}</span>
            ) : (
              <span>{label}</span>
            )}

            {arrow && <RightArrow size="32" height="12" />}
            {icon && RenderIcon(icon, iconSize)}
          </BtnLinkWrapper>
        </StyledButtonLink>
      ) : (
        <StyledRegularButton
          onClick={onClickHandler}
          data-testid={dataTestId}
          className={className}
          variant={variant}
          type={type}
          disabled={disabled}
          style={style}
          //conditionally add the form attribute if it exists
          {...(form && { form: form })}
        >
          <BtnLinkWrapper $iconPosition={iconPosition}>
            {variant === 'iconOnly' ? (
              <span className="visually-hidden">{label}</span>
            ) : (
              <span>{label}</span>
            )}
            {arrow && <RightArrow size="32" height="12" />}
            {icon && RenderIcon(icon, iconSize)}
          </BtnLinkWrapper>
        </StyledRegularButton>
      )}
    </>
  );
};
